import { normalizeError } from 'core/common/errors';
import { CookiesService } from 'core/common/services';
import { Logger } from 'core/common/services/logger/interfaces';
import { StorageInterface } from 'core/common/services/storage';
import { parseQueryParams } from 'core/common/utils/getQueryParams';
import { utmTagsCookieMaxAgeSeconds } from 'core/user/constants';
import { POSSIBLE_UTM_TAGS, UtmSource, UtmTags } from 'core/user/entities/UtmTags';
import { UtmTagsService, UtmTagsStorage } from './UtmTagsService';

export class UtmTagsClientService implements UtmTagsService {
  private readonly cookiesService: CookiesService;

  private readonly logger: Logger;

  private readonly sessionStorage: StorageInterface;

  private readonly storageKey = 'nebula_utm_tags';

  private queryUtmTags: UtmTags = {};

  private constructor(
    cookiesService: CookiesService,
    logger: Logger,
    sessionStorage: StorageInterface,
  ) {
    this.cookiesService = cookiesService;
    this.logger = logger;
    this.sessionStorage = sessionStorage;
  }

  getUtmTags({ storage = 'cookie' }: { storage?: UtmTagsStorage } = {}) {
    return storage === 'session' ? this.getTagsFromSession() : this.getTagsFromCache();
  }

  getQueryUtmTags(): UtmTags {
    return this.queryUtmTags;
  }

  getUtmSource({ storage = 'cookie' }: { storage?: UtmTagsStorage } = {}): UtmSource {
    return this.getUtmTags({ storage }).utm_source ?? UtmSource.NO_SOURCE;
  }

  getUtmCookies(): string {
    return this.getUtmTags().utm_cookies ?? '';
  }

  getUtmContent(): string {
    return this.getUtmTags().utm_content ?? '';
  }

  getUtmAdName(): string {
    return this.getUtmTags().utm_ad_name ?? '';
  }

  static createFromQueryParams(
    cookiesService: CookiesService,
    logger: Logger,
    sessionStorage: StorageInterface,
  ) {
    const utmService = new UtmTagsClientService(cookiesService, logger, sessionStorage);
    utmService.loadFormQueryParams();

    return utmService;
  }

  updateUtmTags(utmTags: UtmTags, storage?: UtmTagsStorage) {
    this.saveToCache({ ...this.getTagsFromCache(), ...utmTags });
    this.saveToSession({ ...this.getTagsFromSession(), ...utmTags });

    return this.getUtmTags({ storage });
  }

  private loadFormQueryParams() {
    const newUtmTags: Record<string, string> = {};

    const parsedQs = parseQueryParams(this.safeDecodeURI(window.location.search));

    POSSIBLE_UTM_TAGS.forEach((utmTag) => {
      if (utmTag in parsedQs) {
        newUtmTags[utmTag] = this.extractUtmTagFromParsedQs(parsedQs[utmTag]);
      }
    });

    this.queryUtmTags = newUtmTags;
    this.updateUtmTags(newUtmTags);
  }

  private saveToSession(utmTags: UtmTags) {
    try {
      this.sessionStorage.set(this.storageKey, utmTags);
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error(error);
    }
  }

  private getTagsFromSession(): UtmTags {
    try {
      const utmTags = this.sessionStorage.get<UtmTags>(this.storageKey) ?? {};

      return this.excludeUtmTags(utmTags, ['quiz']);
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error(error);
      return {};
    }
  }

  private saveToCache(utmTags: UtmTags) {
    try {
      const stringifiedTags = JSON.stringify(utmTags);

      this.cookiesService.set(this.storageKey, stringifiedTags, {
        maxAge: utmTagsCookieMaxAgeSeconds,
        path: '/',
      });
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error(error);
    }
  }

  private getTagsFromCache(): UtmTags {
    try {
      const rawUtmTags = this.cookiesService.get(this.storageKey);
      const utmTags = rawUtmTags ? JSON.parse(rawUtmTags) : {};

      return this.excludeUtmTags(utmTags, ['quiz']);
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error(error);
      return {};
    }
  }

  private extractUtmTagFromParsedQs(parsedQs: undefined | string | string[]): string {
    if (!parsedQs) return '';

    return Array.isArray(parsedQs) ? parsedQs[0] : parsedQs;
  }

  private safeDecodeURI(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  private excludeUtmTags(utmTags: UtmTags, excludeList: Array<string>): UtmTags {
    const newUtmTags: Record<string, string> = {};

    Object.entries(utmTags).forEach(([utmTag, utmValue]) => {
      if (excludeList.includes(utmTag)) return;

      newUtmTags[utmTag] = utmValue;
    });

    return newUtmTags;
  }
}
